'use client';

import Link from 'next/link';
import { cn } from '@/lib/utils';
import React from 'react';
import { useTranslations } from 'next-intl';
import { PlatformLogo } from '@/components/icons/platform-logo';
import { useMarketingNavLinks } from '@/hooks/use-marketing-nav-links';

const sections = [
  { id: 'company-information', title: 'Company Information', color: 'blue' },
  { id: 'the-services', title: 'The Services', color: 'green' },
  { id: 'eligibility', title: 'Eligibility', color: 'orange' },
  { id: 'accounts-and-security', title: 'Accounts and Security', color: 'red' },
  {
    id: 'user-content-and-ownership',
    title: 'User Content and Ownership',
    color: 'purple',
  },
  {
    id: 'acceptable-use-policy',
    title: 'Acceptable Use Policy',
    color: 'indigo',
  },
  {
    id: 'subscriptions-payments-trials',
    title: 'Subscriptions, Payments, and Trials',
    color: 'teal',
  },
  {
    id: 'intellectual-property',
    title: 'Intellectual Property',
    color: 'pink',
  },
  { id: 'api-access', title: 'API Access', color: 'yellow' },
  { id: 'third-party-services', title: 'Third-Party Services', color: 'gray' },
  { id: 'feedback', title: 'Feedback', color: 'blue' },
  { id: 'termination', title: 'Termination', color: 'green' },
  { id: 'disclaimers', title: 'Disclaimers', color: 'orange' },
  {
    id: 'limitation-of-liability',
    title: 'Limitation of Liability',
    color: 'red',
  },
  { id: 'indemnification', title: 'Indemnification', color: 'purple' },
  { id: 'modifications', title: 'Modifications', color: 'indigo' },
  {
    id: 'governing-law-dispute-resolution',
    title: 'Governing Law and Dispute Resolution',
    color: 'teal',
  },
  { id: 'miscellaneous', title: 'Miscellaneous', color: 'pink' },
  { id: 'contact', title: 'Contact', color: 'yellow' },
] as const;

export default function TermsClient() {
  const { navLinks } = useMarketingNavLinks();
  const t = useTranslations('MarketingUI');
  const [active, setActive] = React.useState<string>(sections[0]!.id);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e && e.isIntersecting) {
            setActive((e.target as HTMLElement).id);
          }
        }
      },
      { rootMargin: '-40% 0px -55% 0px', threshold: [0, 0.25, 0.5, 1] }
    );

    const nodes = sections
      .map((s) => document.getElementById(s.id))
      .filter(Boolean) as Element[];
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, []);

  const handleSmoothScroll = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.replaceState(null, '', `#${id}`);
  };

  return (
    <div className="bg-background min-h-dvh">
      <nav
        className="fixed inset-x-0 top-0 z-50 grid grid-cols-2 items-center px-5 py-3 md:grid-cols-[1fr_auto_1fr] md:px-8"
        style={{
          fontFamily: "'Suisse Intl', sans-serif",
          background: '#ffffff',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <Link href="/" aria-label="Home">
          <span style={{ color: '#000000' }}>
            <PlatformLogo size={24} />
          </span>
        </Link>

        <div className="hidden items-center justify-center gap-7 md:flex">
          {navLinks.map((item) => (
            <Link
              key={item.label}
              href={item.href}
              className="rounded-lg px-3 py-1.5 text-[15px] font-normal transition-all duration-200 hover:bg-black/5"
              style={{
                color: '#000000',
                fontFamily: "'Suisse Intl', sans-serif",
              }}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center justify-end gap-1.5 md:gap-2">
          <Link
            href="/signup"
            className="inline-flex items-center rounded-full px-3 py-1.5 text-[11px] font-medium whitespace-nowrap transition-colors duration-300 md:px-5 md:py-2 md:text-[14px]"
            style={{
              background: '#ffffff',
              color: '#000000',
              border: '1px solid rgba(0,0,0,0.1)',
              fontFamily: "'Suisse Intl', sans-serif",
            }}
          >
            {t('signUp')}
          </Link>
          <Link
            href="/login"
            className="inline-flex items-center rounded-full px-3 py-1.5 text-[11px] font-medium whitespace-nowrap transition-colors duration-300 md:px-5 md:py-2 md:text-[14px]"
            style={{
              background: '#000000',
              color: '#ffffff',
              fontFamily: "'Suisse Intl', sans-serif",
            }}
          >
            {t('logIn')}
          </Link>
        </div>
      </nav>

      <div className="container mx-auto max-w-6xl px-4 pt-28 pb-10">
        {/* Header */}
        <div className="mb-10 text-center">
          <div className="border-border bg-muted/40 text-foreground/80 mb-4 inline-flex items-center rounded-full border px-4 py-1.5 text-xs shadow-sm backdrop-blur transition-colors">
            <div className="mr-2 h-1.5 w-1.5 rounded-full bg-green-500" />
            Last updated: October 30, 2025
          </div>
          <h1 className="text-3xl font-bold tracking-tight">
            IMAI STUDIO INC – TERMS OF SERVICE
          </h1>
          <p className="text-muted-foreground mt-1 text-base">
            These Terms of Service govern your access to and use of the
            products, software, websites, mobile applications, APIs, and other
            services provided by IMAI Studio Inc.
          </p>
        </div>

        {/* Introduction */}
        <div className="border-border/60 bg-muted/40 mb-8 rounded-lg border p-6">
          <p className="text-base">
            By creating an account or otherwise using our Services, you agree to
            be bound by these Terms and by our Privacy Policy.
          </p>
          <p className="mt-2 text-base font-semibold">
            If you do not agree, do not access or use our Services.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
          {/* TOC */}
          <aside className="lg:col-span-1">
            <div className="border-border/60 bg-card sticky top-8 rounded-xl border p-4 shadow-sm">
              <div className="mb-2 text-sm font-semibold">
                Table of Contents
              </div>
              <nav className="space-y-1">
                {sections.map((s, i) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    onClick={handleSmoothScroll(s.id)}
                    className={cn(
                      'block rounded-md px-3 py-2 text-sm transition-colors',
                      active === s.id
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {i + 1}. {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <main className="lg:col-span-3">
            <div className="space-y-8">
              <Section
                id="company-information"
                index={1}
                color="blue"
                title="Company Information"
              >
                <p>
                  <strong>IMAI Studio Inc</strong>
                </p>
                <p className="mt-2">
                  1388 Haight Street
                  <br />
                  San Francisco, CA 94117, United States
                </p>
                <p className="mt-2">Delaware-registered C-Corporation</p>
                <p className="mt-2">Contact: contact@imai.studio</p>
              </Section>

              <Section
                id="the-services"
                index={2}
                color="green"
                title="The Services"
              >
                <p>
                  IMAI provides a platform that enables individuals and
                  organizations to:
                </p>
                <ul className="bg-muted/40 mt-4 grid grid-cols-1 gap-2 rounded-lg p-4 sm:grid-cols-2">
                  <li className="text-sm">
                    Design, visualize, and prototype products (such as footwear,
                    apparel, accessories, furnishings, and related lifestyle
                    goods)
                  </li>
                  <li className="text-sm">
                    Generate 2D/3D product renders, marketing visuals, videos,
                    and augmented-reality assets
                  </li>
                  <li className="text-sm">
                    Collaborate in teams, share projects, and use APIs to
                    integrate with third-party tools
                  </li>
                  <li className="text-sm">
                    Manage and export content for creative, commercial, or
                    manufacturing purposes
                  </li>
                </ul>
                <p className="mt-4">
                  We may add, modify, or discontinue features at any time,
                  consistent with applicable law and these Terms.
                </p>
              </Section>

              <Section
                id="eligibility"
                index={3}
                color="orange"
                title="Eligibility"
              >
                <p>
                  You must be at least 13 years old (or the minimum age required
                  in your jurisdiction) to use our Services.
                </p>
                <p className="mt-2">
                  If you are using the Services on behalf of a company or
                  organization, you represent that you have authority to bind
                  that entity to these Terms.
                </p>
              </Section>

              <Section
                id="accounts-and-security"
                index={4}
                color="red"
                title="Accounts and Security"
              >
                <p>You are responsible for:</p>
                <ul className="bg-muted/40 mt-4 grid grid-cols-1 gap-2 rounded-lg p-4">
                  <li className="text-sm">
                    Maintaining the confidentiality of your login credentials
                  </li>
                  <li className="text-sm">
                    All activity that occurs under your account
                  </li>
                  <li className="text-sm">
                    Promptly notifying us of any unauthorized use or security
                    breach
                  </li>
                </ul>
                <p className="mt-4">
                  We reserve the right to suspend or terminate accounts that
                  violate these Terms or our policies.
                </p>
              </Section>

              <Section
                id="user-content-and-ownership"
                index={5}
                color="purple"
                title="User Content and Ownership"
              >
                <h3 className="mt-4 mb-2 text-base font-semibold">
                  5.1. Your Content
                </h3>
                <p>
                  You may upload, input, or otherwise provide materials,
                  including prompts, product designs, files, images, or text
                  (&quot;User Content&quot;).
                </p>
                <p className="mt-2">
                  You retain full ownership of your User Content and of all
                  outputs generated through IMAI Studio&apos;s Services, except
                  where third-party AI model terms (e.g., OpenAI, Replicate,
                  Fal.ai) impose additional restrictions.
                </p>

                <h3 className="mt-6 mb-2 text-base font-semibold">
                  5.2. License to IMAI
                </h3>
                <p>
                  By submitting or creating User Content, you grant IMAI Studio
                  a non-exclusive, worldwide, royalty-free license to host,
                  process, display, and transmit that content solely to operate,
                  improve, and provide the Services.
                </p>
                <p className="mt-2">
                  We do not use your content to train or fine-tune our own AI
                  models.
                </p>

                <h3 className="mt-6 mb-2 text-base font-semibold">
                  5.3. Third-Party AI Processing
                </h3>
                <p>
                  Certain functions use integrated AI providers (including
                  OpenAI, Replicate, Fal.ai). Your prompts and content may be
                  transmitted to these processors solely to generate requested
                  outputs, under their own privacy and usage terms.
                </p>
                <p className="mt-2">
                  You are responsible for ensuring you have the necessary rights
                  to upload and use any materials within the platform.
                </p>
              </Section>

              <Section
                id="acceptable-use-policy"
                index={6}
                color="indigo"
                title="Acceptable Use Policy"
              >
                <p>You agree not to use the Services to:</p>
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                  {[
                    'Violate any law or regulation',
                    'Create, upload, or distribute content that is illegal, infringing, defamatory, hateful, or harassing',
                    'Generate deepfakes, impersonations, or content that misleads others regarding source or identity',
                    'Infringe or misuse trademarks, copyrights, publicity, or privacy rights',
                    "Attempt to reverse-engineer, scrape, or interfere with the platform's integrity",
                    'Circumvent usage limits, access controls, or payment requirements',
                  ].map((t) => (
                    <div key={t} className="bg-primary/5 rounded-lg p-3">
                      <span className="text-sm font-medium">{t}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-4">
                  Violation of this policy may result in suspension or permanent
                  termination of your account.
                </p>
              </Section>

              <Section
                id="subscriptions-payments-trials"
                index={7}
                color="teal"
                title="Subscriptions, Payments, and Trials"
              >
                <h3 className="mt-4 mb-2 text-base font-semibold">
                  7.1. Plans and Billing
                </h3>
                <p>IMAI Studio offers:</p>
                <ul className="bg-muted/40 mt-2 grid grid-cols-1 gap-2 rounded-lg p-4">
                  <li className="text-sm">
                    Subscription plans (monthly or yearly)
                  </li>
                  <li className="text-sm">Pay-per-use credit options</li>
                  <li className="text-sm">
                    Free trials with limited functionality
                  </li>
                </ul>
                <p className="mt-4">
                  Fees, plan features, and payment terms are displayed at
                  purchase. Payments are processed securely by our partner
                  Stripe or other authorized processors.
                </p>

                <h3 className="mt-6 mb-2 text-base font-semibold">
                  7.2. Renewals and Cancellations
                </h3>
                <p>
                  Subscriptions automatically renew unless cancelled before the
                  renewal date.
                </p>
                <p className="mt-2">
                  You may cancel at any time through your account dashboard;
                  cancellation prevents future charges but does not refund prior
                  payments unless required by law.
                </p>

                <h3 className="mt-6 mb-2 text-base font-semibold">
                  7.3. Taxes and Refunds
                </h3>
                <p>
                  Prices are exclusive of taxes unless stated otherwise. Refunds
                  are granted only as required by applicable law or explicit
                  IMAI Studio policy.
                </p>
              </Section>

              <Section
                id="intellectual-property"
                index={8}
                color="pink"
                title="Intellectual Property"
              >
                <p>
                  All intellectual property rights in the Services—including
                  software, interfaces, templates, and trademarks (&quot;IMAI
                  Marks&quot;)—belong to IMAI Studio Inc or its licensors.
                </p>
                <p className="mt-2">
                  Except as expressly permitted, you may not copy, modify,
                  distribute, sell, or lease any part of our software or
                  branding.
                </p>
                <p className="mt-2">
                  You may display the IMAI name or marks only with prior written
                  authorization.
                </p>
              </Section>

              <Section
                id="api-access"
                index={9}
                color="yellow"
                title="API Access"
              >
                <p>If you use IMAI&apos;s APIs:</p>
                <ul className="bg-muted/40 mt-4 grid grid-cols-1 gap-2 rounded-lg p-4">
                  <li className="text-sm">
                    You must comply with our technical documentation and usage
                    limits
                  </li>
                  <li className="text-sm">
                    You may not use APIs for competing products or to resell
                    access
                  </li>
                  <li className="text-sm">
                    We may revoke API credentials at any time for abuse or
                    security reasons
                  </li>
                </ul>
                <p className="mt-4">
                  Additional API-specific terms may apply and will be
                  incorporated by reference.
                </p>
              </Section>

              <Section
                id="third-party-services"
                index={10}
                color="gray"
                title="Third-Party Services"
              >
                <p>
                  Our Services may link to or integrate with third-party
                  products or platforms.
                </p>
                <p className="mt-2">
                  IMAI Studio is not responsible for their availability,
                  accuracy, or data practices.
                </p>
                <p className="mt-2">
                  Your use of third-party services is governed by their
                  respective terms and privacy policies.
                </p>
              </Section>

              <Section id="feedback" index={11} color="blue" title="Feedback">
                <p>
                  If you provide suggestions, comments, or ideas
                  (&quot;Feedback&quot;), you grant IMAI Studio a perpetual,
                  irrevocable, royalty-free license to use such Feedback for any
                  purpose, without compensation or obligation.
                </p>
              </Section>

              <Section
                id="termination"
                index={12}
                color="green"
                title="Termination"
              >
                <p>We may suspend or terminate your access immediately if:</p>
                <ul className="bg-muted/40 mt-4 grid grid-cols-1 gap-2 rounded-lg p-4">
                  <li className="text-sm">You breach these Terms</li>
                  <li className="text-sm">We are required by law to do so</li>
                  <li className="text-sm">
                    Continuing the Service becomes impractical due to business
                    or technical reasons
                  </li>
                </ul>
                <p className="mt-4">
                  Upon termination, your right to use the Services ceases
                  immediately, though certain sections (e.g., Ownership,
                  Liability, Governing Law) will continue to apply.
                </p>
              </Section>

              <Section
                id="disclaimers"
                index={13}
                color="orange"
                title="Disclaimers"
              >
                <p>
                  The Services are provided &quot;as is&quot; and &quot;as
                  available&quot;, without warranties of any kind, express or
                  implied.
                </p>
                <p className="mt-2">
                  IMAI Studio disclaims all warranties of merchantability,
                  fitness for a particular purpose, and non-infringement.
                </p>
                <p className="mt-2">
                  We do not guarantee that outputs generated through AI models
                  will be accurate, unique, or free from third-party claims.
                </p>
              </Section>

              <Section
                id="limitation-of-liability"
                index={14}
                color="red"
                title="Limitation of Liability"
              >
                <p>To the fullest extent permitted by law:</p>
                <p className="mt-2">
                  IMAI Studio&apos;s total liability arising from or related to
                  the Services shall not exceed the amount you paid to IMAI in
                  the 12 months preceding the claim.
                </p>
                <p className="mt-2">
                  IMAI Studio shall not be liable for indirect, incidental,
                  consequential, or punitive damages, including lost profits or
                  data.
                </p>
                <p className="mt-2">
                  Some jurisdictions do not allow such exclusions; in those
                  cases, limitations apply only to the extent allowed by law.
                </p>
              </Section>

              <Section
                id="indemnification"
                index={15}
                color="purple"
                title="Indemnification"
              >
                <p>
                  You agree to indemnify and hold harmless IMAI Studio, its
                  affiliates, officers, employees, and partners from any claims,
                  damages, or expenses (including reasonable attorneys&apos;
                  fees) arising out of:
                </p>
                <ul className="bg-muted/40 mt-4 grid grid-cols-1 gap-2 rounded-lg p-4">
                  <li className="text-sm">Your use of the Services</li>
                  <li className="text-sm">Your User Content</li>
                  <li className="text-sm">
                    Your violation of these Terms or third-party rights
                  </li>
                </ul>
              </Section>

              <Section
                id="modifications"
                index={16}
                color="indigo"
                title="Modifications"
              >
                <p>We may revise these Terms from time to time.</p>
                <p className="mt-2">
                  If we make material changes, we will notify you by email or
                  in-app notice.
                </p>
                <p className="mt-2">
                  Continued use of the Services after the effective date
                  constitutes acceptance of the updated Terms.
                </p>
              </Section>

              <Section
                id="governing-law-dispute-resolution"
                index={17}
                color="teal"
                title="Governing Law and Dispute Resolution"
              >
                <p>
                  These Terms and any related dispute are governed by the laws
                  of the State of Delaware, USA, excluding conflict-of-law
                  principles.
                </p>
                <p className="mt-2">
                  All disputes shall be brought exclusively in the federal or
                  state courts located in Delaware, and you consent to their
                  jurisdiction.
                </p>
              </Section>

              <Section
                id="miscellaneous"
                index={18}
                color="pink"
                title="Miscellaneous"
              >
                <p>
                  <strong>Entire Agreement:</strong> These Terms and the Privacy
                  Policy constitute the complete agreement between you and IMAI
                  Studio.
                </p>
                <p className="mt-2">
                  <strong>Severability:</strong> If any provision is held
                  invalid, the remainder remains in effect.
                </p>
                <p className="mt-2">
                  <strong>Assignment:</strong> You may not assign these Terms
                  without our prior consent.
                </p>
                <p className="mt-2">
                  <strong>Waiver:</strong> Failure to enforce a provision is not
                  a waiver of future enforcement.
                </p>
                <p className="mt-2">
                  <strong>Headings:</strong> Used only for convenience and do
                  not affect interpretation.
                </p>
              </Section>

              <Section id="contact" index={19} color="yellow" title="Contact">
                <p>
                  For questions regarding these Terms or the Services, contact:
                </p>
                <p className="mt-4">
                  <strong>IMAI Studio Inc</strong>
                  <br />
                  1388 Haight Street, San Francisco, CA 94117 USA
                  <br />
                  Email: contact@imai.studio
                </p>
              </Section>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

type SectionProps = {
  id: string;
  title: string;
  index: number;
  color:
    | 'blue'
    | 'green'
    | 'orange'
    | 'red'
    | 'purple'
    | 'indigo'
    | 'teal'
    | 'pink'
    | 'yellow'
    | 'gray';
  children: React.ReactNode;
};

function Section({ id, title, index, color, children }: SectionProps) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) setVisible(entry.isIntersecting);
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const colorMap: Record<string, string> = {
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
    green:
      'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400',
    orange:
      'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-400',
    red: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400',
    purple:
      'bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-400',
    indigo:
      'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-400',
    teal: 'bg-teal-100 text-teal-700 dark:bg-teal-500/15 dark:text-teal-400',
    pink: 'bg-pink-100 text-pink-700 dark:bg-pink-500/15 dark:text-pink-400',
    yellow:
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-300',
    gray: 'bg-gray-100 text-gray-700 dark:bg-gray-500/15 dark:text-gray-400',
  };

  return (
    <section
      id={id}
      ref={ref}
      className={cn(
        'border-border/60 bg-card rounded-2xl border p-6 shadow-sm transition-all duration-500',
        visible ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
      )}
    >
      <div className="mb-4 flex items-center">
        <div
          className={cn(
            'mr-3 flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold',
            colorMap[color]
          )}
        >
          {index}
        </div>
        <h2 className="text-xl leading-none font-semibold tracking-tight">
          {title}
        </h2>
      </div>
      <div className="prose prose-sm prose-neutral dark:prose-invert max-w-none">
        {children}
      </div>
    </section>
  );
}
